import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ClientSideAnnotationSystem } from '../components/annotations'
import { apiClient } from '../lib/api-client'

interface Session {
  id: string
  session_code: string
  room_name: string
  customer_note?: string
  status: 'active' | 'ended'
  participant_count: number
  created_at: string
  join_url?: string
}

interface Organization {
  id: string
  name: string
  plan_id: string
  subscription_status: string
  settings: Record<string, any>
}

interface Usage {
  sessions_used: number
  session_limit: number
  billing_cycle_end: string
}

const Agent = (): JSX.Element => {
  const [sessions, setSessions] = useState<Session[]>([])
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [usage, setUsage] = useState<Usage | null>(null)
  const [isCreatingSession, setIsCreatingSession] = useState(false)
  const [customerNote, setCustomerNote] = useState('')
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const navigate = useNavigate()

  // Load agent data
  useEffect(() => {
    loadAgentData()
  }, [])

  const loadAgentData = async (): Promise<void> => {
    try {
      // TODO: Replace with real API calls
      setOrganization({
        id: 'demo-org-123',
        name: 'Demo Organization',
        plan_id: 'pro',
        subscription_status: 'active',
        settings: { url_shortener_enabled: true }
      })

      setUsage({
        sessions_used: 7,
        session_limit: 50,
        billing_cycle_end: new Date(Date.now() + 23 * 24 * 60 * 60 * 1000).toISOString()
      })

      // Load existing sessions
      setSessions([
        {
          id: 'session-1',
          session_code: 'ABC123',
          room_name: 'room-abc123',
          customer_note: 'Customer having trouble with login screen',
          status: 'active',
          participant_count: 1,
          created_at: new Date(Date.now() - 300000).toISOString(),
          join_url: `${window.location.origin}/join/ABC123`
        }
      ])
    } catch (error) {
      console.error('Failed to load agent data:', error)
    }
  }

  const createSession = async (): Promise<void> => {
    if (!organization || !usage) return
    
    if (usage.sessions_used >= usage.session_limit) {
      alert('Monthly session limit reached. Please upgrade your plan.')
      return
    }

    setIsCreatingSession(true)
    try {
      const response = await apiClient.createSession({
        title: 'Support Session',
        description: customerNote || undefined,
        customerName: undefined,
        customerEmail: undefined
      })
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create session')
      }
      
      const sessionData = response.data as any
      const newSession: Session = {
        id: sessionData.session.id,
        session_code: sessionData.session.session_code,
        room_name: sessionData.session.room_name,
        customer_note: sessionData.session.customer_note,
        status: sessionData.session.status as 'active' | 'ended',
        participant_count: sessionData.session.participant_count,
        created_at: sessionData.session.created_at,
        join_url: sessionData.join_url
      }
      
      setSessions(prev => [newSession, ...prev])
      setCustomerNote('')
      
      // Update usage from response
      if (sessionData.usage) {
        setUsage({
          sessions_used: sessionData.usage.sessions_used,
          session_limit: sessionData.usage.session_limit,
          billing_cycle_end: sessionData.usage.billing_cycle_end
        })
      }

      // Auto-join the session
      setActiveSessionId(newSession.id)
      
    } catch (error) {
      console.error('Failed to create session:', error)
      alert('Failed to create session. Please try again.')
    } finally {
      setIsCreatingSession(false)
    }
  }

  const joinSession = (sessionId: string): void => {
    setActiveSessionId(sessionId)
    // TODO: Initialize LiveKit connection
  }

  const endSession = async (sessionId: string): Promise<void> => {
    try {
      // TODO: Replace with real API call
      setSessions(prev => prev.map(session => 
        session.id === sessionId 
          ? { ...session, status: 'ended' as const } 
          : session
      ))
      
      if (activeSessionId === sessionId) {
        setActiveSessionId(null)
      }
    } catch (error) {
      console.error('Failed to end session:', error)
      alert('Failed to end session. Please try again.')
    }
  }

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text)
      // TODO: Replace with proper toast notification
      alert(`Copied to clipboard: ${text}`)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        document.execCommand('copy')
        alert(`Copied to clipboard: ${text}`)
      } catch (err) {
        alert('Failed to copy to clipboard')
      }
      textArea.remove()
    }
  }

  const shareWithCollaborator = (sessionCode: string): void => {
    const message = `Hi! Please join my support session using code: ${sessionCode}\n\nGo to ${window.location.origin} and enter this code in the Collaborator section.`
    
    if (navigator.share) {
      navigator.share({
        title: 'Join Support Session',
        text: message
      })
    } else {
      copyToClipboard(message)
    }
  }

  if (!organization) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading agent dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Agent Dashboard</h1>
              <p className="text-sm text-gray-600">{organization.name}</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm text-gray-600">
                  Sessions: <span className="font-semibold">{usage?.sessions_used}/{usage?.session_limit}</span>
                </div>
                <div className="text-sm text-gray-600">
                  Plan: <span className="font-semibold capitalize">{organization.plan_id}</span>
                </div>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Session Creation */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New Session</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Issue Description
                  </label>
                  <textarea
                    value={customerNote}
                    onChange={(e) => setCustomerNote(e.target.value)}
                    placeholder="Describe the customer's issue to help collaborators understand the context..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                  />
                </div>
                <button
                  onClick={createSession}
                  disabled={isCreatingSession || (usage ? usage.sessions_used >= usage.session_limit : false)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isCreatingSession ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating Session...
                    </>
                  ) : (
                    'Create New Session'
                  )}
                </button>
                {usage && usage.sessions_used >= usage.session_limit && (
                  <p className="text-sm text-red-600">
                    Monthly session limit reached. Please contact your administrator to upgrade your plan.
                  </p>
                )}
              </div>
            </div>

            {/* Active Session View */}
            {activeSessionId && (
              <div className="bg-white rounded-lg shadow p-6 mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Active Session</h2>
                  <button
                    onClick={() => endSession(activeSessionId)}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm"
                  >
                    End Session
                  </button>
                </div>
                
                {/* Hidden video element for LiveKit - will be visible when connected */}
                <video
                  ref={videoRef}
                  className="hidden"
                  autoPlay
                  muted
                  playsInline
                />

                {/* Annotation System with Screenshot & Drawing Tools */}
                <ClientSideAnnotationSystem
                  videoElement={videoRef.current}
                  width={800}
                  height={450}
                  onMemoryWarning={(usage) => {
                    if (usage.totalMB > 150) {
                      alert(`Memory usage high: ${usage.totalMB.toFixed(1)}MB. Consider deleting some screenshots.`)
                    }
                  }}
                  className="mb-4"
                />

                {/* Session Status */}
                <div className="flex justify-between items-center bg-gray-50 p-3 rounded-md">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-gray-700">Session Active</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Participants: 1 (waiting for customer)
                  </div>
                </div>
              </div>
            )}

            {/* My Sessions */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">My Sessions</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {sessions.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    No sessions created yet. Create your first session above!
                  </div>
                ) : (
                  sessions.map((session) => (
                    <div key={session.id} className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <span className="text-xl font-mono font-bold text-blue-600">
                              {session.session_code}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              session.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {session.status}
                            </span>
                            <span className="text-sm text-gray-500">
                              {session.participant_count} participant{session.participant_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                          {session.customer_note && (
                            <p className="mt-1 text-sm text-gray-600">{session.customer_note}</p>
                          )}
                          <p className="text-xs text-gray-500">
                            Created: {new Date(session.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          {session.status === 'active' && (
                            <>
                              <button
                                onClick={() => joinSession(session.id)}
                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                Join
                              </button>
                              <button
                                onClick={() => shareWithCollaborator(session.session_code)}
                                className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                              >
                                Share Code
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => copyToClipboard(session.session_code)}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                          >
                            Copy Code
                          </button>
                          {session.join_url && (
                            <button
                              onClick={() => copyToClipboard(session.join_url!)}
                              className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                            >
                              Copy Link
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/supervisor')}
                  className="w-full text-left px-4 py-3 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg"
                >
                  🔍 View as Supervisor
                </button>
                <button
                  onClick={() => navigate('/collaborator')}
                  className="w-full text-left px-4 py-3 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg"
                >
                  🤝 Join as Collaborator
                </button>
                <button className="w-full text-left px-4 py-3 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg">
                  📊 View Analytics
                </button>
              </div>
            </div>

            {/* Usage Statistics */}
            {usage && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">This Month</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Sessions Created</span>
                    <span className="font-semibold">{usage.sessions_used}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Limit</span>
                    <span className="font-semibold">{usage.session_limit}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${Math.min((usage.sessions_used / usage.session_limit) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500">
                    Resets: {new Date(usage.billing_cycle_end).toLocaleDateString()}
                  </div>
                </div>
              </div>
            )}

            {/* Tips */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">Agent Tips</h3>
              <div className="space-y-3 text-sm text-blue-800">
                <p>💡 Share session codes with collaborators to get expert help</p>
                <p>🎯 Use clear issue descriptions to help team members assist faster</p>
                <p>📱 Customer links work on any mobile device - no app required</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Agent
